/**
 * `<RemoveMemberDialog>` — confirm before removing a workspace member.
 *
 * Per `functional-design/business-rules.md § decision rules` (last-admin
 * protection). The API enforces; the UI surfaces the resulting
 * `LAST_ADMIN` code via the central errors mapping when applicable.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";

export interface RemoveMemberDialogProps {
  workspaceSlug: string;
  memberId: string;
  memberEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RemoveMemberDialog({
  workspaceSlug,
  memberId,
  memberEmail,
  open,
  onOpenChange,
}: RemoveMemberDialogProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => authClient.removeMember(workspaceSlug, memberId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceSlug] });
      toast.success(t("members.remove.toast"));
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("members.remove.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`remove-member-dialog-${memberId}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("members.remove.confirmTitle", { email: memberEmail })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t("members.remove.confirmBody")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={mutation.isPending}
            data-testid={`remove-member-cancel-${memberId}`}
          >
            {t("members.remove.confirmNo")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
            disabled={mutation.isPending}
            data-testid={`remove-member-confirm-${memberId}`}
          >
            {t("members.remove.confirmYes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
